import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { usePageEvent, track } from "../lib/tracker";
import {
  Building2, GraduationCap, Users, Mail, Copy, RefreshCcw, Trash2,
  Loader2, CheckCircle2, Clock, XCircle, AlertTriangle, Sparkles,
  Calendar, ArrowRight, Send,
} from "lucide-react";
import { toast } from "sonner";
import { apiErrorToast } from "../lib/utils";

export default function OrgDashboard() {
  usePageEvent("org_dashboard_view");
  const { user } = useAuth();
  const [params] = useSearchParams();
  const welcome = params.get("welcome") === "1";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);           // { organization, licenses, invitations, server_time }
  const [invitations, setInvitations] = useState([]);
  const [emailsText, setEmailsText] = useState("");
  const [inviteMsg, setInviteMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [notAdmin, setNotAdmin] = useState(false);

  const loadAll = async () => {
    try {
      const [dash, inv] = await Promise.all([
        api.get("/org/dashboard"),
        api.get("/org/invitations"),
      ]);
      setData(dash.data);
      setInvitations(inv.data.items || []);
      setNotAdmin(false);
    } catch (e) {
      if (e?.response?.status === 403) setNotAdmin(true);
      else apiErrorToast(e, "Could not load organization");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const org = data?.organization;
  const stats = data?.licenses || { purchased: 0, used: 0, available: 0, expired: 0, assigned: 0, consumed: 0 };
  const inviteStats = data?.invitations || { pending: 0, accepted: 0, revoked: 0, expired: 0 };
  const Icon = org?.account_type === "institution" ? GraduationCap : Building2;
  const noun = org?.account_type === "institution" ? "student" : "candidate";
  const nounPlural = org?.account_type === "institution" ? "students" : "candidates";

  const parsedEmails = useMemo(() => {
    return (emailsText || "")
      .split(/[\s,;]+/).map(s => s.trim()).filter(Boolean)
      .filter(e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
  }, [emailsText]);
  const invalidEmails = useMemo(() => {
    return (emailsText || "")
      .split(/[\s,;]+/).map(s => s.trim()).filter(Boolean)
      .filter(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
  }, [emailsText]);

  const sendInvites = async (e) => {
    e?.preventDefault?.();
    if (parsedEmails.length === 0) { toast.error("Add at least one valid email"); return; }
    if (parsedEmails.length > stats.available) {
      toast.error(`Only ${stats.available} licenses available (you entered ${parsedEmails.length}).`);
      return;
    }
    setSending(true);
    try {
      const { data: r } = await api.post("/org/invite", {
        emails: parsedEmails,
        message: inviteMsg || undefined,
      });
      track("org_invite_sent", { count: r.count });
      toast.success(`${r.count} invitation${r.count === 1 ? "" : "s"} created`);
      setEmailsText("");
      setInviteMsg("");
      await loadAll();
    } catch (e) { apiErrorToast(e, "Could not create invitations"); }
    finally { setSending(false); }
  };

  const revokeInvite = async (invId) => {
    setRevokingId(invId);
    try {
      await api.post(`/org/invitations/${invId}/revoke`);
      toast.success("Invitation revoked, license returned to pool.");
      await loadAll();
    } catch (e) { apiErrorToast(e, "Could not revoke"); }
    finally { setRevokingId(null); }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/signup?invite=${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Invite link copied");
    } catch { toast.error("Could not copy — select & copy manually: " + url); }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center gap-2 text-zinc-500" data-testid="org-dashboard-loading">
        <Loader2 className="animate-spin" size={16} /> Loading your organization…
      </div>
    );
  }

  if (notAdmin || !org) {
    return (
      <div className="p-6 md:p-10 max-w-2xl" data-testid="org-dashboard-empty">
        <div className="rounded-xl border border-zinc-200 bg-white p-6">
          <div className="h-10 w-10 rounded-md bg-indigo-100 text-indigo-600 flex items-center justify-center">
            <Building2 size={18} />
          </div>
          <h1 className="font-display font-black text-2xl mt-4">No organization yet</h1>
          <p className="text-sm text-zinc-600 mt-2">
            This page is for Corporate &amp; Institution admins. Purchase a Corporate or
            Institution plan to invite {nounPlural} by email, track license usage and
            manage your team.
          </p>
          <Link to="/app/pricing?category=corporate"
                data-testid="org-dashboard-goto-pricing"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md btn-gradient text-white font-semibold text-sm">
            See Corporate &amp; Institution Plans <ArrowRight size={14}/>
          </Link>
        </div>
      </div>
    );
  }

  const expiryDate = (org.expiry_date || "").slice(0, 10);
  const pct = stats.purchased ? Math.round((stats.used / stats.purchased) * 100) : 0;

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="org-dashboard">
      {welcome && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3" data-testid="org-welcome-banner">
          <Sparkles size={16} className="text-emerald-700 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-emerald-900">Welcome to your {org.account_type} dashboard!</div>
            <div className="text-emerald-800">
              {stats.purchased} licenses have been credited and are valid until <strong>{expiryDate}</strong>.
              Invite {nounPlural} below to start using them.
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
            <Icon size={12}/> {org.account_type === "institution" ? "Institution" : "Corporate"} dashboard
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-2" data-testid="org-name-heading">
            {org.name}
          </h1>
          <div className="text-sm text-zinc-600 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span><strong>{org.plan_name}</strong> plan</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><Calendar size={12}/> Valid until <strong>{expiryDate}</strong></span>
            <span>·</span>
            <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={12}/> {org.status}</span>
          </div>
        </div>
        <button
          data-testid="org-refresh-btn"
          onClick={loadAll}
          className="self-start inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm border border-zinc-300 hover:bg-zinc-50">
          <RefreshCcw size={14}/> Refresh
        </button>
      </div>

      {/* License stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="org-license-stats">
        <StatCard testid="stat-purchased" label="Purchased" value={stats.purchased} icon={Users} tone="indigo"/>
        <StatCard testid="stat-used" label="Used" value={stats.used} icon={CheckCircle2} tone="emerald"
                  sub={stats.purchased ? `${pct}% utilised` : undefined}/>
        <StatCard testid="stat-available" label="Available" value={stats.available} icon={Mail} tone="sky"/>
        <StatCard testid="stat-expired" label="Expired" value={stats.expired} icon={AlertTriangle} tone="rose"/>
      </div>

      {/* Utilization bar */}
      <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="org-utilization">
        <div className="flex items-center justify-between text-xs text-zinc-500 mb-2">
          <span>Seat utilisation</span>
          <span>{stats.used} / {stats.purchased}</span>
        </div>
        <div className="w-full h-2 bg-zinc-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500 transition-all"
               style={{ width: `${pct}%` }}/>
        </div>
        <div className="mt-2 text-xs text-zinc-500">
          <span className="mr-3"><span className="inline-block h-2 w-2 rounded-full bg-amber-400 mr-1"/>Assigned {stats.assigned}</span>
          <span><span className="inline-block h-2 w-2 rounded-full bg-emerald-500 mr-1"/>Consumed {stats.consumed}</span>
        </div>
      </div>

      {/* Invite form */}
      <div className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="org-invite-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display font-black text-xl">Invite {nounPlural}</h2>
            <p className="text-sm text-zinc-600 mt-1">
              Paste emails below (comma, space or new-line separated). Each invite reserves 1 license
              and is consumed when the {noun} signs up with that email.
            </p>
          </div>
          <div className="text-xs text-zinc-500">
            <div><strong className="text-zinc-800">{stats.available}</strong> licenses available</div>
            {inviteStats.pending > 0 && <div>{inviteStats.pending} pending invite{inviteStats.pending === 1 ? "" : "s"}</div>}
          </div>
        </div>
        <form onSubmit={sendInvites} className="mt-4 space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Emails</label>
            <textarea
              data-testid="org-invite-emails"
              rows={4}
              value={emailsText}
              onChange={e => setEmailsText(e.target.value)}
              placeholder={`alex@example.com\npriya@example.com\nnikhil@example.com`}
              className="mt-1 w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono resize-y focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
            <div className="mt-1 text-xs flex flex-wrap gap-x-4 gap-y-1">
              <span className={parsedEmails.length ? "text-emerald-700" : "text-zinc-500"}>
                {parsedEmails.length} valid email{parsedEmails.length === 1 ? "" : "s"}
              </span>
              {invalidEmails.length > 0 && (
                <span className="text-rose-600" data-testid="org-invite-invalid">
                  {invalidEmails.length} invalid will be skipped
                </span>
              )}
              {parsedEmails.length > stats.available && (
                <span className="text-rose-600 font-semibold" data-testid="org-invite-overflow">
                  Exceeds {stats.available} available licenses
                </span>
              )}
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
              Personal note <span className="normal-case tracking-normal text-zinc-400">(optional, shown on signup)</span>
            </label>
            <input
              data-testid="org-invite-message"
              maxLength={300}
              value={inviteMsg}
              onChange={e => setInviteMsg(e.target.value)}
              placeholder={`Welcome to ${org.name}'s hiring pipeline`}
              className="mt-1 w-full h-10 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <button
            data-testid="org-invite-submit"
            disabled={sending || parsedEmails.length === 0 || parsedEmails.length > stats.available}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-gradient text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            {sending ? <><Loader2 className="animate-spin" size={14}/> Creating invites…</>
                     : <><Send size={14}/> Send {parsedEmails.length || ""} invite{parsedEmails.length === 1 ? "" : "s"}</>}
          </button>
        </form>
      </div>

      {/* Invitations list */}
      <div className="rounded-xl border border-zinc-200 bg-white" data-testid="org-invitations-card">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="font-display font-black text-xl">Invitations</h2>
            <p className="text-xs text-zinc-500">
              Pending {inviteStats.pending} · Accepted {inviteStats.accepted} · Revoked {inviteStats.revoked} · Expired {inviteStats.expired}
            </p>
          </div>
        </div>
        {invitations.length === 0 ? (
          <div className="p-6 text-sm text-zinc-500" data-testid="org-invitations-empty">
            No invitations yet. Send your first batch above.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {invitations.map(inv => (
              <InvitationRow key={inv.id} inv={inv}
                             onCopy={() => copyLink(inv.token)}
                             onRevoke={() => revokeInvite(inv.id)}
                             revoking={revokingId === inv.id}/>
            ))}
          </div>
        )}
      </div>

      <div className="text-xs text-zinc-500">
        Signed in as <strong>{user?.email}</strong> · Organization admin.{" "}
        <Link to="/app/pricing" className="text-indigo-600 font-semibold">See plans &amp; add-ons</Link>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone = "indigo", sub, testid }) {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-700",
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{label}</div>
        <div className={`h-7 w-7 rounded-md flex items-center justify-center ${tones[tone]}`}>
          <Icon size={14}/>
        </div>
      </div>
      <div className="mt-2 font-display font-black text-3xl text-zinc-900">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function InvitationRow({ inv, onCopy, onRevoke, revoking }) {
  const statusInfo = {
    pending: { icon: Clock, text: "Pending", cls: "text-amber-700 bg-amber-50 border-amber-200" },
    accepted: { icon: CheckCircle2, text: "Accepted", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    revoked: { icon: XCircle, text: "Revoked", cls: "text-zinc-700 bg-zinc-100 border-zinc-200" },
    expired: { icon: AlertTriangle, text: "Expired", cls: "text-rose-700 bg-rose-50 border-rose-200" },
  }[inv.status] || { icon: Clock, text: inv.status, cls: "text-zinc-700 bg-zinc-100 border-zinc-200" };
  const S = statusInfo.icon;
  const date = (inv.invited_at || "").slice(0, 10);
  const expiry = (inv.expires_at || "").slice(0, 10);
  return (
    <div className="px-5 py-3 flex items-center justify-between flex-wrap gap-3" data-testid={`org-invitation-${inv.id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm truncate" data-testid={`org-invitation-email-${inv.id}`}>{inv.email}</div>
        <div className="text-xs text-zinc-500 mt-0.5">
          Invited {date}{inv.status === "pending" && expiry && ` · Expires ${expiry}`}
        </div>
      </div>
      <div className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border font-semibold uppercase tracking-wider ${statusInfo.cls}`}>
        <S size={11}/> {statusInfo.text}
      </div>
      <div className="flex items-center gap-2">
        {inv.status === "pending" && (
          <>
            <button data-testid={`org-invitation-copy-${inv.id}`}
                    onClick={onCopy}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-zinc-200 hover:bg-zinc-50">
              <Copy size={12}/> Copy link
            </button>
            <button data-testid={`org-invitation-revoke-${inv.id}`}
                    onClick={onRevoke}
                    disabled={revoking}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-50">
              {revoking ? <Loader2 className="animate-spin" size={12}/> : <Trash2 size={12}/>} Revoke
            </button>
          </>
        )}
      </div>
    </div>
  );
}
